import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { downloadPDF } from "../../lib/pdf";
import { QRCodeCanvas } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Save, Loader2, Trash2, Activity, Droplet, Share2, Download } from "lucide-react";

const PERM = {
  upper: [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28],
  lower: [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38],
};
const TEMP = {
  upper: [55, 54, 53, 52, 51, 61, 62, 63, 64, 65],
  lower: [85, 84, 83, 82, 81, 71, 72, 73, 74, 75],
};
const SITE_LABELS = ["DV", "V", "MV", "DP", "P", "MP"]; // Disto/Vestib/Mesio vestibular · Disto/Palatino/Mesio palatino

function pdColor(v) {
  const n = Number(v);
  if (!n) return "";
  if (n >= 6) return "bg-red-100 text-red-700 border-red-300";
  if (n >= 4) return "bg-amber-100 text-amber-700 border-amber-300";
  return "bg-emerald-50 text-emerald-700 border-emerald-200";
}

function chartStats(teeth) {
  let sites = 0, deep = 0, bop = 0, maxPd = 0;
  Object.values(teeth || {}).forEach((t) => {
    (t.pd || []).forEach((v) => { const n = Number(v); if (n) { sites++; if (n >= 4) deep++; if (n > maxPd) maxPd = n; } });
    (t.bop || []).forEach((b) => { if (b) bop++; });
  });
  return { sites, deep, bop, maxPd, bopPct: sites ? Math.round((bop / sites) * 100) : 0 };
}

function pdSvgColor(v) {
  const n = Number(v);
  if (!n) return "#F3F4F6";
  if (n >= 6) return "#FEE2E2";
  if (n >= 4) return "#FEF3C7";
  return "#DCFCE7";
}

// Genera una imagen SVG limpia del sondaje para compartir/imprimir.
function buildPerioSvg(teeth, dentition, list, notes) {
  const withData = list.filter((n) => { const t = teeth[n]; return t && ((t.pd || []).some((x) => Number(x)) || (t.bop || []).some(Boolean) || t.mob); });
  const rowH = 26, headH = 90, pad = 16, cell = 26, labelW = 34;
  const W = 560;
  const H = headH + withData.length * rowH + (notes ? 60 : 24) + 40;
  const s = chartStats(teeth);
  let el = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"><rect width="${W}" height="${H}" fill="#ffffff"/>`;
  el += `<text x="${pad}" y="28" font-family="Arial" font-size="16" font-weight="bold" fill="#3D405B">Sondaje periodontal (${dentition === "temporal" ? "Temporal" : "Permanente"})</text>`;
  el += `<text x="${pad}" y="48" font-family="Arial" font-size="11" fill="#6B7280">Sitios ≥4mm: ${s.deep}   ·   Índice de sangrado: ${s.bopPct}%   ·   Profundidad máx: ${s.maxPd} mm</text>`;
  // Column headers
  const x0 = pad + labelW;
  el += `<text x="${pad}" y="${headH - 8}" font-family="Arial" font-size="10" font-weight="bold" fill="#9CA3AF">Diente</text>`;
  ["DV", "V", "MV", "DP", "P", "MP"].forEach((lbl, i) => {
    el += `<text x="${x0 + i * cell + cell / 2}" y="${headH - 8}" font-family="Arial" font-size="9" fill="#9CA3AF" text-anchor="middle">${lbl}</text>`;
  });
  el += `<text x="${x0 + 6 * cell + 30}" y="${headH - 8}" font-family="Arial" font-size="9" fill="#9CA3AF" text-anchor="middle">BOP</text>`;
  el += `<text x="${x0 + 6 * cell + 90}" y="${headH - 8}" font-family="Arial" font-size="9" fill="#9CA3AF" text-anchor="middle">Mov</text>`;
  withData.forEach((num, ri) => {
    const t = teeth[num]; const y = headH + ri * rowH;
    el += `<text x="${pad}" y="${y + 17}" font-family="Arial" font-size="11" font-weight="bold" fill="#3D405B">${num}</text>`;
    (t.pd || [0, 0, 0, 0, 0, 0]).forEach((v, i) => {
      const cx = x0 + i * cell;
      el += `<rect x="${cx + 1}" y="${y + 2}" width="${cell - 3}" height="20" rx="3" fill="${pdSvgColor(v)}" stroke="#E5E7EB" stroke-width="0.5"/>`;
      el += `<text x="${cx + cell / 2 - 0.5}" y="${y + 16}" font-family="Arial" font-size="11" fill="#374151" text-anchor="middle">${Number(v) || ""}</text>`;
    });
    const bopN = (t.bop || []).filter(Boolean).length;
    el += `<text x="${x0 + 6 * cell + 30}" y="${y + 16}" font-family="Arial" font-size="11" fill="${bopN ? "#DC2626" : "#9CA3AF"}" text-anchor="middle">${bopN || "—"}</text>`;
    el += `<text x="${x0 + 6 * cell + 90}" y="${y + 16}" font-family="Arial" font-size="11" fill="#374151" text-anchor="middle">M${t.mob || 0}</text>`;
  });
  if (notes) {
    const ny = headH + withData.length * rowH + 18;
    el += `<text x="${pad}" y="${ny}" font-family="Arial" font-size="10" font-weight="bold" fill="#3D405B">Notas: </text>`;
    el += `<text x="${pad + 40}" y="${ny}" font-family="Arial" font-size="10" fill="#374151">${(notes || "").slice(0, 120).replace(/[<>&]/g, "")}</text>`;
  }
  return el + `</svg>`;
}

async function svgToPng(svgStr) {
  const url = URL.createObjectURL(new Blob([svgStr], { type: "image/svg+xml" }));
  try {
    const img = new Image();
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    const scale = 2;
    const canvas = document.createElement("canvas");
    canvas.width = img.width * scale; canvas.height = img.height * scale;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale); ctx.drawImage(img, 0, 0);
    return canvas.toDataURL("image/png");
  } finally { URL.revokeObjectURL(url); }
}

function ToothRow({ num, data, onPd, onBop, onMob }) {
  const pd = data.pd || [0, 0, 0, 0, 0, 0];
  const bop = data.bop || [false, false, false, false, false, false];
  const mob = data.mob || 0;
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-stone-100 last:border-0" data-testid={`perio-tooth-${num}`}>
      <span className="w-8 text-center text-xs font-bold text-brand-ink tabular-nums shrink-0">{num}</span>
      <div className="flex gap-1">
        {pd.map((v, i) => (
          <input key={i} data-testid={`perio-pd-${num}-${i}`} type="number" min="0" max="15" value={v || ""}
            onChange={(e) => onPd(num, i, e.target.value)} title={SITE_LABELS[i]}
            className={`w-8 h-8 text-center text-xs font-semibold rounded-md border outline-none focus:border-brand-sage tabular-nums ${pdColor(v) || "border-stone-200 bg-white"}`} />
        ))}
      </div>
      <div className="flex gap-0.5 ml-1">
        {bop.map((b, i) => (
          <button key={i} data-testid={`perio-bop-${num}-${i}`} onClick={() => onBop(num, i)} title={`Sangrado ${SITE_LABELS[i]}`}
            className={`w-3.5 h-3.5 rounded-full border transition-all ${b ? "bg-red-500 border-red-500" : "bg-white border-stone-300 hover:border-red-300"}`} />
        ))}
      </div>
      <select data-testid={`perio-mob-${num}`} value={mob} onChange={(e) => onMob(num, Number(e.target.value))}
        className="ml-1 text-xs rounded-md border border-stone-200 py-1 px-1.5 outline-none focus:border-brand-sage bg-white">
        {[0, 1, 2, 3].map((m) => <option key={m} value={m}>M{m}</option>)}
      </select>
    </div>
  );
}

export default function PerioChart({ patient }) {
  const [teeth, setTeeth] = useState({});
  const [notes, setNotes] = useState("");
  const [dentition, setDentition] = useState(patient?.age?.months != null && patient.age.months < 72 ? "temporal" : "permanente");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/patients/${patient.patient_id}/perio`);
      setHistory(r.data || []);
    } catch { toast.error("No se pudo cargar el sondaje"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patient.patient_id]);

  const teethList = dentition === "temporal" ? TEMP : PERM;
  const setTooth = (num, patch) => {
    setTeeth((t) => {
      const cur = { pd: [0, 0, 0, 0, 0, 0], bop: [false, false, false, false, false, false], mob: 0, ...(t[num] || {}) };
      return { ...t, [num]: { ...cur, ...patch } };
    });
    setDirty(true);
  };
  const onPd = (num, i, val) => setTooth(num, { pd: Object.assign([...(teeth[num]?.pd || [0, 0, 0, 0, 0, 0])], { [i]: val === "" ? 0 : Number(val) }) });
  const onBop = (num, i) => setTooth(num, { bop: Object.assign([...(teeth[num]?.bop || [false, false, false, false, false, false])], { [i]: !(teeth[num]?.bop?.[i]) }) });
  const onMob = (num, m) => setTooth(num, { mob: m });

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      Object.entries(teeth).forEach(([k, v]) => {
        const hasData = (v.pd || []).some((x) => Number(x)) || (v.bop || []).some(Boolean) || v.mob;
        if (hasData) clean[k] = v;
      });
      await api.post(`/patients/${patient.patient_id}/perio`, { dentition, teeth: clean, notes });
      toast.success("Sondaje guardado ✓"); setDirty(false); setTeeth({}); setNotes("");
      load();
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const loadPast = (chart) => {
    setTeeth(chart.teeth || {}); setNotes(chart.notes || ""); setDentition(chart.dentition || "permanente");
    setDirty(true);
    toast.info("Cargado del historial (se guardará como una visita nueva)");
  };
  const del = async (chartId) => {
    if (!window.confirm("¿Eliminar esta carta de sondaje?")) return;
    await api.delete(`/patients/${patient.patient_id}/perio/${chartId}`);
    load(); toast.success("Eliminado");
  };

  const stats = useMemo(() => chartStats(teeth), [teeth]);

  const share = async () => {
    const hasData = Object.values(teeth).some((v) => (v.pd || []).some((x) => Number(x)) || (v.bop || []).some(Boolean) || v.mob);
    if (!hasData) { toast.error("Captura al menos un valor antes de compartir"); return; }
    setSharing(true);
    try {
      const list = [...(dentition === "temporal" ? TEMP : PERM).upper, ...(dentition === "temporal" ? TEMP : PERM).lower];
      const png = await svgToPng(buildPerioSvg(teeth, dentition, list, notes));
      const r = await api.post(`/patients/${patient.patient_id}/perio/share`, { image: png, notes });
      setShared(r.data);
      toast.success("Sondaje adjuntado al expediente ✓");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo compartir"); }
    finally { setSharing(false); }
  };

  return (
    <div className="space-y-5" data-testid="perio-panel">
      <Dialog open={!!shared} onOpenChange={(o) => !o && setShared(null)}>
        <DialogContent className="rounded-3xl max-w-sm" data-testid="perio-share-dialog">
          <DialogHeader><DialogTitle className="font-outfit text-brand-ink">Sondaje listo</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Sondaje periodontal adjuntado al expediente y listo para compartir por QR.</DialogDescription>
          <div className="text-center">
            {shared && (
              <div className="inline-block bg-white p-3 rounded-2xl border-2 border-brand-terracotta/20">
                <QRCodeCanvas value={`${window.location.origin}/receta/${shared.share_id}`} size={160} fgColor="#C4623F" level="M" />
              </div>
            )}
            <p className="text-sm text-muted-foreground mt-3">Escanea el QR para verlo en el teléfono. Ya quedó guardado como documento en el expediente.</p>
            <button data-testid="perio-download-pdf" onClick={() => downloadPDF(shared, `${window.location.origin}/receta/${shared.share_id}`)}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-brand-terracotta text-white font-outfit font-semibold py-3 rounded-2xl hover:bg-brand-terracottaDark transition-all">
              <Download size={17} /> Descargar / Imprimir PDF
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-2" data-testid="perio-dentition-toggle">
          {[["temporal", "Temporal"], ["permanente", "Permanente"]].map(([k, l]) => (
            <button key={k} data-testid={`perio-dentition-${k}`} onClick={() => setDentition(k)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${dentition === k ? "bg-brand-ink text-white" : "bg-white border border-stone-200 text-brand-ink/70 hover:border-brand-ink/40"}`}>{l}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="perio-share-btn" onClick={share} disabled={sharing}
            className="flex items-center gap-2 font-outfit font-semibold px-4 py-2.5 rounded-2xl bg-white border border-stone-200 text-brand-ink hover:border-brand-sage transition-all disabled:opacity-60">
            {sharing ? <Loader2 size={17} className="animate-spin" /> : <Share2 size={17} />} Compartir / Imprimir
          </button>
          <button data-testid="perio-save-btn" onClick={save} disabled={saving || !dirty}
            className={`flex items-center gap-2 font-outfit font-semibold px-5 py-2.5 rounded-2xl transition-all ${dirty ? "bg-brand-terracotta text-white hover:bg-brand-terracottaDark" : "bg-stone-100 text-brand-ink/40"}`}>
            {saving ? <Loader2 size={17} className="animate-spin" /> : <Save size={17} />} Guardar visita
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-100 p-3 text-[11px] text-brand-ink/60 flex flex-wrap gap-x-4 gap-y-1">
        <span className="font-semibold text-brand-ink">Profundidad de bolsa (mm)</span> por 6 sitios: {SITE_LABELS.join(" · ")}.
        <span className="flex items-center gap-1"><Droplet size={12} className="text-red-500" /> punto = sangrado al sondaje</span>
        <span className="flex items-center gap-1"><Activity size={12} /> M0–M3 = movilidad</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300" /> ≥4mm</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-100 border border-red-300" /> ≥6mm</span>
      </div>

      {/* Resumen en vivo */}
      <div className="flex flex-wrap gap-2" data-testid="perio-live-stats">
        <span className="text-xs font-semibold bg-stone-50 rounded-full px-3 py-1.5">Sitios ≥4mm: <b className="text-amber-600">{stats.deep}</b></span>
        <span className="text-xs font-semibold bg-stone-50 rounded-full px-3 py-1.5">Índice de sangrado: <b className="text-red-600">{stats.bopPct}%</b></span>
        <span className="text-xs font-semibold bg-stone-50 rounded-full px-3 py-1.5">Profundidad máx: <b>{stats.maxPd} mm</b></span>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-gradient-to-b from-brand-sand/20 to-white rounded-2xl border border-stone-100 p-3">
          <p className="text-xs font-bold text-brand-ink/50 uppercase tracking-wide mb-1 pl-9">Arcada superior</p>
          {teethList.upper.map((num) => (
            <ToothRow key={num} num={num} data={teeth[num] || {}} onPd={onPd} onBop={onBop} onMob={onMob} />
          ))}
        </div>
        <div className="bg-gradient-to-b from-brand-sand/20 to-white rounded-2xl border border-stone-100 p-3">
          <p className="text-xs font-bold text-brand-ink/50 uppercase tracking-wide mb-1 pl-9">Arcada inferior</p>
          {teethList.lower.map((num) => (
            <ToothRow key={num} num={num} data={teeth[num] || {}} onPd={onPd} onBop={onBop} onMob={onMob} />
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-100 p-4">
        <p className="font-outfit font-bold text-brand-ink text-sm mb-2">Notas periodontales</p>
        <textarea data-testid="perio-notes" value={notes} onChange={(e) => { setNotes(e.target.value); setDirty(true); }} rows={2}
          placeholder="Diagnóstico periodontal, plan, observaciones…" className="w-full px-3 py-2 rounded-xl border border-stone-200 outline-none focus:border-brand-sage text-sm" />
      </div>

      {/* Historial / comparación entre visitas */}
      <div className="bg-white rounded-2xl border border-stone-100 p-4" data-testid="perio-history">
        <p className="font-outfit font-bold text-brand-ink text-sm mb-3">Historial de sondajes</p>
        {loading ? (
          <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-brand-sage" size={22} /></div>
        ) : history.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aún no hay cartas de sondaje. Registra la primera visita.</p>
        ) : (
          <div className="space-y-2">
            {history.map((h) => {
              const s = chartStats(h.teeth);
              return (
                <div key={h.chart_id} data-testid={`perio-hist-${h.chart_id}`} className="flex items-center justify-between gap-3 bg-stone-50 rounded-xl px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-brand-ink">{new Date(h.date).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })}</p>
                    <p className="text-xs text-muted-foreground">Sitios ≥4mm: {s.deep} · Sangrado: {s.bopPct}% · Máx: {s.maxPd}mm</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`perio-load-${h.chart_id}`} onClick={() => loadPast(h)} className="text-xs font-semibold text-brand-sage hover:underline">Cargar</button>
                    <button data-testid={`perio-del-${h.chart_id}`} onClick={() => del(h.chart_id)} className="text-brand-ink/40 hover:text-red-500 transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
